import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import ProbeInput from "../components/ProbeInput";
import ResultsTable from "../components/ResultsTable";
import RoutingInsight from "../components/RoutingInsight";
import ScorePanel from "../components/ScorePanel";
import CertPanel from "../components/CertPanel";
import CachePanel from "../components/CachePanel";
import PaywallModal from "../components/PaywallModal";
import { createProbe, getProbe, ApiError } from "../lib/api";
import { ProbeRun, ALL_REGIONS } from "../types";
import { SECURITY_CHECKS } from "../components/ScorePanel";

const ANON_RUN_KEY = "wrtt_anon_runs";
const GATED_KEY = "wrtt_gated";
const ANON_LIMIT = 3;

export default function Home() {
  const { isSignedIn, isLoaded } = useAuth();

  const [runId, setRunId] = useState<string | null>(null);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [enabledChecks, setEnabledChecks] = useState<Set<string>>(
    () => new Set(SECURITY_CHECKS.map((c) => c.id))
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggleCheck(id: string) {
    setEnabledChecks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn) {
      localStorage.removeItem(GATED_KEY);
      localStorage.removeItem(ANON_RUN_KEY);
      return;
    }
    if (localStorage.getItem(GATED_KEY) === "true") {
      setShowPaywall(true);
    }
  }, [isLoaded, isSignedIn]);

  const { data: run } = useQuery<ProbeRun>({
    queryKey: ["probe", runId],
    queryFn: () => getProbe(runId!),
    enabled: !!runId,
    retry: 1,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === "complete" || data?.status === "error") return false;
      if (query.state.error) return false;
      return 500;
    },
  });

  const runRef = useRef<ProbeRun | undefined>(undefined);
  useEffect(() => { runRef.current = run; }, [run]);

  useEffect(() => {
    if (!runId) return;
    const timer = setTimeout(() => {
      const s = runRef.current?.status;
      if (s === "complete" || s === "error") return;
      setRunId(null);
      setError("Probe timed out. Please try again.");
      setIsSubmitting(false);
    }, 45_000);
    return () => clearTimeout(timer);
  }, [runId]);

  async function handleSubmit(params: {
    url: string;
    method: string;
    headers: Record<string, string>;
    body: string;
    regions: string[];
  }) {
    if (!isSignedIn) {
      if (localStorage.getItem(GATED_KEY) === "true") {
        setShowPaywall(true);
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);
    setRunId(null);
    setSelectedRegions(params.regions);

    try {
      const result = await createProbe({
        url: params.url,
        method: params.method,
        headers: Object.keys(params.headers).length ? params.headers : undefined,
        body: params.body || undefined,
        regions: params.regions,
      });

      if (!isSignedIn) {
        const count = parseInt(localStorage.getItem(ANON_RUN_KEY) ?? "0", 10) + 1;
        localStorage.setItem(ANON_RUN_KEY, String(count));
        if (count >= ANON_LIMIT) {
          localStorage.setItem(GATED_KEY, "true");
        }
      }

      setRunId(result.run_id);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.isGated) {
          setShowPaywall(true);
          localStorage.setItem(GATED_KEY, "true");
        } else {
          setError(err.message);
        }
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const isComplete = run?.status === "complete" || run?.status === "error";

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>

      {/* Centered pillar */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: "36px 20px 80px" }}>
        <div className="glass" style={{ width: "100%", maxWidth: 760, padding: "44px 36px 40px", animation: "hero-enter 0.55s ease-out both" }}>

          {/* Hero */}
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <h1 style={{
              fontSize: "clamp(28px, 5vw, 50px)",
              fontWeight: 700,
              margin: "0 0 14px",
              color: "#FFFFFF",
              letterSpacing: "-0.025em",
              fontFamily: "'Bricolage Grotesque', sans-serif",
              animation: "hero-enter 0.65s 0.05s ease-out both",
            }}>
              Global API Latency in Seconds
            </h1>
            <p style={{
              fontSize: 16,
              color: "#98A1AB",
              margin: 0,
              lineHeight: 1.65,
              animation: "hero-enter 0.65s 0.18s ease-out both",
            }}>
              Test any HTTP endpoint from 8 regions simultaneously. Real RTT data, no account required.
            </p>
          </div>

          {/* Probe input */}
          <ProbeInput
            onSubmit={handleSubmit}
            isLoading={isSubmitting}
            enabledChecks={enabledChecks}
            onToggleCheck={toggleCheck}
          />

          {error && (
            <div style={{
              marginTop: 16, padding: "12px 16px",
              background: "rgba(255,92,92,0.1)",
              border: "1px solid rgba(255,92,92,0.3)",
              borderRadius: 3, color: "#FF5C5C", fontSize: 14,
            }}>
              {error}
            </div>
          )}

          {/* Results — same card, separated by a rule */}
          {run && (
            <div style={{ marginTop: 32, paddingTop: 28, borderTop: "1px solid rgba(255,255,255,0.07)" }}>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div>
                  <p style={{ margin: 0, fontSize: 12, color: "#98A1AB", textTransform: "uppercase", letterSpacing: "0.05em" }}>Testing</p>
                  <p style={{ margin: "3px 0 0", fontWeight: 600, color: "#EFF2F5", wordBreak: "break-all" }}>{run.url}</p>
                </div>
                {isComplete && (
                  <Link to={`/r/${run.run_id}`} style={{ fontSize: 13, color: "#98A1AB", whiteSpace: "nowrap", marginLeft: 16 }}>
                    Share results →
                  </Link>
                )}
              </div>

              {isComplete && run.percentiles && (
                <div style={{
                  display: "flex", gap: 32, marginBottom: 20, padding: "14px 20px",
                  background: "rgba(18,18,18,0.6)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 3,
                }}>
                  {([["P50", run.percentiles.p50], ["P95", run.percentiles.p95], ["P99", run.percentiles.p99]] as [string, number | null][]).map(([label, val]) => (
                    <div key={label}>
                      <p style={{ margin: 0, fontSize: 11, color: "#98A1AB", fontWeight: 700, letterSpacing: "0.05em" }}>{label}</p>
                      <p style={{ margin: "3px 0 0", fontSize: 22, fontWeight: 800, color: "#EFF2F5" }}>
                        {val != null ? `${val}ms` : "—"}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <ResultsTable
                results={run.results ?? []}
                regionCount={run.region_count}
                regionsDone={run.regions_done}
                selectedRegions={selectedRegions}
              />

              <RoutingInsight results={run.results ?? []} />

              {isComplete && (run.results ?? []).length > 0 && (
                <CachePanel results={run.results ?? []} />
              )}

              {isComplete && (run.results ?? []).length > 0 && (
                <CertPanel results={run.results ?? []} />
              )}

              {isComplete && (run.results ?? []).length > 0 && (
                <ScorePanel
                  results={run.results ?? []}
                  selectedRegions={selectedRegions.length > 0 ? selectedRegions : ALL_REGIONS}
                  enabledChecks={enabledChecks}
                  onToggleCheck={toggleCheck}
                />
              )}
            </div>
          )}

        </div>
      </div>

      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} />}
    </div>
  );
}
